import {
  BulkCreateAttendanceShiftShiftsInput,
  HalfDayShiftDocument,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import { Moment } from 'moment'

import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction from 'modules/core/lib/asyncAction'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getDateRange } from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/lib/getDateRange'
import { useShiftActions } from 'modules/attendance/hooks/shifts/useShiftActions'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'

interface UseHalfDayShiftsParams {
  year: number
  employeeId: number
  currentMonth: Moment
}

export const useHalfDayShifts = ({ year, employeeId, currentMonth }: UseHalfDayShiftsParams) => {
  const { bulkDestroyShift, bulkCreateShift } = useShiftActions()

  const { data, refetch, loading } = useQuery(HalfDayShiftDocument, {
    variables: { year, employeeId },
    fetchPolicy: 'cache-and-network',
  })

  const halfDayShiftData = data?.attendance.halfDayShiftsConnection.nodes[0]

  const stats = {
    total: halfDayShiftData?.total ?? 0,
    remainingWorkDays: halfDayShiftData?.remainingWorkDays ?? 0,
    totalPaidAbsences: halfDayShiftData?.totalPaidAbsences ?? 0,
    totalWorkedDays: halfDayShiftData ? halfDayShiftData.halfDayWorked / 2 : 0,
  }

  const submitBulkCreate = async (shiftsToCreate: TimesheetPageShiftFragment[]) => {
    app.setProgress({
      message: i18n.t('common.loading'),
    })

    const createShifts: BulkCreateAttendanceShiftShiftsInput[] = shiftsToCreate.map((shift) => ({
      clockIn: shift.clockIn,
      clockOut: shift.clockOut,
      date: shift.date,
      day: stringToDate(shift.date, 'backend').date(),
      employeeId: shift.employeeId,
      halfDay: shift.halfDay,
      locationType: AttendanceShiftUtils.locationType(shift),
      observations: shift.observations,
      referenceDate: shift.referenceDate,
      source: 'desktop',
      timeSettingsBreakConfigurationId: shift.timeSettingsBreakConfiguration?.id,
      workable: shift.workable,
    }))

    const { success } = await asyncAction(
      () =>
        bulkCreateShift({
          shifts: createShifts,
        }),
      {
        successMessage: i18n.t('clock_in.bulk_action.create_success_message', {
          count: shiftsToCreate.length / 2,
        }),
        errorFlash: true,
      }
    )
    app.setProgress(null)

    if (success) {
      refetch()
    }
  }

  const submitBulkDestroy = async (shiftsToDestroy: TimesheetPageShiftFragment[]) => {
    const ids = shiftsToDestroy.map((shift) => shift.id)

    const dateRange = getDateRange(currentMonth)
    const { startOn, endOfMonth: endOn } = dateRange

    const payload = {
      ids,
      employeeId,
      startOn,
      endOn,
    }

    app.setProgress({
      message: i18n.t('common.loading'),
    })

    const { success } = await asyncAction(() => bulkDestroyShift(payload), {
      successMessage: i18n.t('clock_in.bulk_action.destroy_success_message', {
        count: shiftsToDestroy.length / 2,
      }),
      errorFlash: true,
    })

    app.setProgress(null)

    if (success) {
      refetch()
    }
  }

  const submitBulkDailyShiftChange = (shiftsToChange: TimesheetPageShiftFragment[]) => {
    if (!shiftsToChange.length) return

    const sortedShifts = shiftsToChange.sort(
      (a, b) => new Date(a.date).valueOf() - new Date(b.date).valueOf()
    )

    const isHalfDayShift = (shift: TimesheetPageShiftFragment | undefined) =>
      shift ? !AttendanceShiftUtils.isNew(shift) : false

    const isDefaultShift = (shift: TimesheetPageShiftFragment | undefined) =>
      shift ? AttendanceShiftUtils.isNew(shift) : false

    if (isHalfDayShift(sortedShifts[0])) {
      submitBulkDestroy(sortedShifts.filter(isHalfDayShift))
    } else {
      submitBulkCreate(sortedShifts.filter(isDefaultShift))
    }
  }

  return {
    submitBulkDailyShiftChange,
    stats,
    loading,
    refetch,
  }
}
